import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Header from '@/components/Header';
import CustomModelManager from '@/components/CustomModelManager';
import CustomTemperatureModelManager from '@/components/CustomTemperatureModelManager';
import OnboardingVideoModal from '@/components/OnboardingVideoModal';
import WelcomeFirstRunModal from '@/components/WelcomeFirstRunModal';
import NewFeatureBanner from '@/components/NewFeatureBanner';
import LiveCageKPIsSection from '@/components/LiveCageKPIsSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Fish, Camera, TrendingUp, History, Calculator, FileSpreadsheet, Clock, AlertTriangle, RefreshCw, PlayCircle, Sparkles, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [recentRecords, setRecentRecords] = useState([]);
  const [showUpdatePanel, setShowUpdatePanel] = useState(false);
  // ★ Onboarding video — auto-show on first mount when user hasn't opted out
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // ★ First-run welcome modal — auto-opens on first dashboard visit when
  //   the user hasn't yet chosen their preferred entry path
  //   (spreadsheet uploader vs snapshot sandbox).
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  useEffect(() => {
    if (token && showUpdatePanel) {
      axios.get(`${API}/all-records-summary`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setRecentRecords(res.data.records || []))
        .catch(() => {});
    }
  }, [token, showUpdatePanel]);

  // Auto-open onboarding video when the user hasn't permanently dismissed
  // it. Priority:
  //   1. user.hide_onboarding_video === true  → never show (opt-out honored)
  //   2. user.onboarding_video_seen_at is falsy → FIRST-TIME user → always
  //      show on login, ignoring sessionStorage (a stale flag must never
  //      block a brand-new user from the tour)
  //   3. Otherwise show once per login (sessionStorage gate), since the
  //      session flag is cleared on login/logout in App.js.
  useEffect(() => {
    if (user == null) return;
    if (user?.hide_onboarding_video) return;
    const firstTime = !user?.onboarding_video_seen_at;
    if (!firstTime) {
      try {
        if (sessionStorage.getItem('pfd_onboarding_shown_this_session') === '1') return;
      } catch { /* ignore — privacy mode */ }
    }
    try {
      sessionStorage.setItem('pfd_onboarding_shown_this_session', '1');
    } catch { /* ignore */ }
    setOnboardingOpen(true);
  }, [user]);

  // First-run welcome modal — now shows on EVERY login unless the user
  // has ticked "Don't show me this again" inside the modal. Read-only
  // users skip it (they can't run analyses yet).
  useEffect(() => {
    if (user == null) return;
    if (user?.dont_show_welcome_again) return;
    if (user?.is_read_only) return;
    // Defer slightly so the video modal opens first if both apply.
    const t = setTimeout(() => setWelcomeOpen(true), 250);
    return () => clearTimeout(t);
  }, [user]);

  const handleWelcomePath = (path) => {
    setWelcomeOpen(false);
    if (path === 'spreadsheet') {
      // Land on the spreadsheet uploader with the curated Seabream demo
      // auto-loaded (the page reads ?startExample=Seabream).
      navigate('/spreadsheet-comparison?startExample=Seabream');
    } else {
      navigate('/snapshot');
    }
  };
  
  // Check if user is pending approval (not approved, not unlimited, not admin)
  const isPendingApproval = !user?.is_approved && !user?.is_unlimited && !user?.is_admin;

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="dashboard">
      <Header />
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Pending Approval Banner */}
        {isPendingApproval && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4" data-testid="pending-approval-banner">
            <div className="flex items-start gap-3">
              <Clock className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-bold text-amber-800">
                  Account Pending Approval
                </p>
                <p className="text-sm text-amber-700">
                  Your 10 free analyses are pending admin approval. You will receive an email notification once your account has been approved.
                </p>
                <p className="text-sm text-amber-700">
                  In the meantime, you can explore the features below in <span className="font-semibold">read-only mode</span>.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0891b2] dark:text-cyan-300 mb-2">
            Precision Fish Detection (PFD) Analysis
          </h1>
          <p className="text-gray-600 dark:text-slate-300">Welcome back, {user?.email}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            {isPendingApproval ? (
              <span className="font-semibold text-amber-600">
                <Clock className="w-4 h-4 inline mr-1" />
                Pending Approval — 5 Free Analyses Available After Approval
              </span>
            ) : user?.is_unlimited ? (
              <span className="font-semibold text-[#0891b2]">Unlimited Access</span>
            ) : (
              <>
                Free analyses remaining: <span className="font-semibold text-[#0891b2]">{user?.free_analysis_credits || 0}</span>
              </>
            )}
          </p>
        </div>

        {/* Dismissible "new feature" announcement — points at the raw
            AquaManager / Fishtalk Excel direct upload capability.
            Dismissal is persisted to localStorage so it stays hidden. */}
        {!user?.is_restricted_demo && <NewFeatureBanner />}

        {/* First-run hero CTA — shown to users who haven't completed
            a full analysis yet (no welcome path chosen yet, OR they
            picked one but never ran anything). Vanishes once any
            analysis exists or the user explicitly dismisses it. */}
        {!user?.is_read_only && (user?.total_analyses ?? 0) === 0 && (
          <Card
            className="border-2 border-cyan-400 bg-gradient-to-br from-cyan-50 via-white to-teal-50 dark:from-cyan-900/30 dark:via-slate-900 dark:to-teal-900/30 shadow-md cursor-pointer hover:shadow-xl transition-all relative overflow-hidden"
            onClick={() => navigate('/spreadsheet-comparison?startExample=Seabream')}
            data-testid="first-run-hero-cta"
          >
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-cyan-200/30 dark:bg-cyan-500/10 rounded-full pointer-events-none" />
            <CardHeader>
              <div className="flex items-start sm:items-center gap-4 flex-col sm:flex-row">
                <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 shadow-md">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-cyan-700 dark:text-cyan-300 flex items-center gap-2 flex-wrap">
                    See what PFD does in 30 seconds
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-600 text-white">
                      Recommended
                    </span>
                  </CardTitle>
                  <CardDescription className="text-slate-700 dark:text-slate-300 mt-1">
                    You haven&apos;t run a full analysis yet. Open the curated
                    Seabream cycle to see Missing Fish Intelligence, Harvest
                    Correction signals and the full growth trajectory live.
                  </CardDescription>
                </div>
                <Button
                  className="bg-cyan-600 hover:bg-cyan-700 text-white shadow self-stretch sm:self-auto"
                  data-testid="first-run-hero-cta-btn"
                >
                  Open live demo
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </CardHeader>
          </Card>
        )}

        {/* Live Cage KPIs — admin-only. Rendered ABOVE the module
            tiles so operational status is the first thing an admin
            sees on the dashboard. The component internally guards on
            user?.is_admin and returns null for non-admins. */}
        <LiveCageKPIsSection />

        <div className="grid md:grid-cols-2 gap-6">
          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/snapshot')}
            data-testid="snapshot-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#0891b2] rounded-xl">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#0891b2]">Snapshot Analysis</CardTitle>
                  <CardDescription className="text-gray-600">One-time FCR analysis</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Perform a single point-in-time FCR analysis to identify missing fish and get actionable recommendations.
              </p>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/growth-model')}
            data-testid="growth-model-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#0e7490] rounded-xl">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#0e7490]">Growth vs Model</CardTitle>
                  <CardDescription className="text-gray-600">Compare actual vs expected growth</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Compare actual growth curve against internal model to identify performance trends and deviations.
              </p>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/sfr-calculator')}
            data-testid="sfr-calculator-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#8b5cf6] rounded-xl">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#8b5cf6]">SFR Calculator</CardTitle>
                  <CardDescription className="text-gray-600">Feed rate recommendations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Calculate daily feed recommendations based on temperature, weight, and fish count for Seabream, Seabass, and Tilapia.
              </p>
            </CardContent>
          </Card>


          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/growth-prognosis')}
            data-testid="growth-prognosis-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#10b981] rounded-xl">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#10b981]">Growth Prognosis</CardTitle>
                  <CardDescription className="text-gray-600">Future growth prediction</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Predict days to target weight, feed requirements, and final biomass using SGR-based growth modeling.
              </p>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/history')}
            data-testid="history-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#0ea5e9] rounded-xl">
                  <History className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#0ea5e9]">Analysis History</CardTitle>
                  <CardDescription className="text-gray-600">View past analyses</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Browse and review all previous FCR analyses and recommendations.
              </p>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/spreadsheet-comparison')}
            data-testid="spreadsheet-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#f59e0b] rounded-xl">
                  <FileSpreadsheet className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#f59e0b]">Spreadsheet Comparison</CardTitle>
                  <CardDescription className="text-gray-600">Upload production data</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Upload your production spreadsheet to compare actual growth vs model predictions.
              </p>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-slate-200 cursor-pointer hover:border-[#0891b2] hover:shadow-lg transition-all"
            onClick={() => navigate('/multi-profile')}
            data-testid="multi-profile-card"
          >
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#6366f1] rounded-xl">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-[#6366f1]">Multi-Profile Comparison</CardTitle>
                  <CardDescription className="text-gray-600">Compare multiple cages</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Compare up to 10 cages or batches side-by-side against the internal growth model.
              </p>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-teal-300 bg-gradient-to-br from-teal-50 to-emerald-50 relative overflow-hidden"
            data-testid="wingman-intro-card"
            hidden={user?.is_restricted_demo}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-100/40 rounded-full -translate-y-10 translate-x-10" />
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-white">
                    <path d="M12 2C6.48 2 2 5.58 2 10c0 2.24 1.12 4.26 2.92 5.7L3 22l4.35-2.18C8.78 20.27 10.35 20.5 12 20.5c5.52 0 10-3.08 10-7.25S17.52 2 12 2z" />
                  </svg>
                </div>
                <div>
                  <CardTitle className="text-teal-700">PFD Wingman AI</CardTitle>
                  <CardDescription className="text-teal-600/80">Aquaculture intelligence at your fingertips</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 relative">
              <p className="text-sm text-gray-700 leading-relaxed">
                Wingman combines GPT-5.2 with aquaculture journals — giving you instant, science-backed analysis on every page. Ask anything, from species benchmarks to cage management.
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                  <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">Aquaculture Science</span> — Species-specific FCR, SGR, feeding rates, mortality norms, and stocking density references built in</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">Contextual Insights</span> — One-click AI interpretation comparing your results against industry benchmarks</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">Proactive Alerts</span> — Auto-detects concerning patterns like missing fish, overfeeding, or trajectory jumps</p>
                </div>
              </div>
              <p className="text-[11px] text-teal-600 font-medium pt-1">
                Powered by GPT-5.2 + aquaculture journals — click the chat icon to start
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Update Existing Record Section */}
        <Card className="mt-8 border-slate-200 shadow-md" data-testid="update-record-card">
          <button
            onClick={() => setShowUpdatePanel(!showUpdatePanel)}
            className="w-full"
          >
            <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-base">Update Existing Record</CardTitle>
                    <CardDescription>Extend a saved record with new data — track how estimates evolve over time</CardDescription>
                  </div>
                </div>
                <span className="text-xs text-slate-400">{showUpdatePanel ? 'Hide' : 'Show'}</span>
              </div>
            </CardHeader>
          </button>
          {showUpdatePanel && (
            <CardContent className="pt-0 pb-4">
              {recentRecords.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">No saved records found. Upload data in Spreadsheet or Growth vs Model first.</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {recentRecords.map(r => (
                    <button
                      key={`${r.type}-${r.id}`}
                      onClick={() => navigate(r.type === 'spreadsheet' ? '/spreadsheet-comparison' : '/growth-model', { state: { loadRecordId: r.id } })}
                      className="w-full text-left px-4 py-3 border border-slate-200 rounded-lg hover:border-violet-300 hover:bg-violet-50/30 transition-all group"
                      data-testid={`update-record-${r.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                              r.type === 'spreadsheet' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {r.type === 'spreadsheet' ? 'Spreadsheet' : 'Growth'}
                            </span>
                            <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span>{r.species || 'Unknown species'}</span>
                            <span>{r.sample_count} samples</span>
                            {r.revision_count > 0 && <span className="text-violet-500">{r.revision_count} revision{r.revision_count > 1 ? 's' : ''}</span>}
                            {r.mfi_count > 0 && <span className="text-amber-500">{r.mfi_count} MFI run{r.mfi_count > 1 ? 's' : ''}</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className="text-[10px] text-slate-400">Last updated</p>
                          <p className="text-xs text-slate-600">{r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Custom Model Manager Section */}
        <div className="mt-8">
          <CustomModelManager />
        </div>

        {/* Temperature Model Upload Section */}
        <div className="mt-6">
          <CustomTemperatureModelManager />
        </div>

        {/* Re-watch onboarding video */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-2 px-2">
          <p className="text-xs text-slate-500">New here? Re-watch the 2-minute walkthrough whenever you need it.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOnboardingOpen(true)}
            data-testid="rewatch-onboarding-btn"
          >
            <PlayCircle className="w-4 h-4 mr-1.5" /> Watch onboarding video
          </Button>
        </div>
      </div>

      {/* Onboarding video modal */}
      <OnboardingVideoModal
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />

      {/* First-run welcome modal — only shows once per account */}
      <WelcomeFirstRunModal
        open={welcomeOpen}
        onPathChosen={handleWelcomePath}
      />
    </div>
  );
}
